// Import Firebase functions
import { initializeApp } from "firebase/app";
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getAnalytics, isSupported } from "firebase/analytics";
import logger from "./utils/logger";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyB_ayFrPWgOhCHoI6-01k51j4U4zckw7IQ",
  authDomain: "ebook-planner-pro.firebaseapp.com",
  projectId: "ebook-planner-pro",
  storageBucket: "ebook-planner-pro.firebasestorage.app",
  messagingSenderId: "850649393353",
  appId: "1:850649393353:web:d73fa3870a7000fb294518",
  measurementId: "G-NKTKVES2ZF",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const analyticsPromise = isSupported()
  .then((supported) => (supported ? getAnalytics(app) : null))
  .catch(() => null);

// Set up auth persistence with proper error handling and retry logic
export async function setupAuthPersistence() {
  try {
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error("Persistence setup timeout")), 10000); // 10 second timeout
    });

    const persistencePromise = (async () => {
      try {
        // Try to set local persistence first (survives browser restarts)
        await setPersistence(auth, browserLocalPersistence);
        logger.log(
          "✅ Auth persistence set to LOCAL (will persist across browser sessions)"
        );
        return true;
      } catch (localError) {
        logger.warn(
          "⚠️ Local persistence failed, trying session persistence:",
          localError.message
        );

        try {
          // Fallback to session persistence (survives page refreshes but not browser restarts)
          await setPersistence(auth, browserSessionPersistence);
          logger.log(
            "✅ Auth persistence set to SESSION (will persist during browser session)"
          );
          return true;
        } catch (sessionError) {
          logger.warn(
            "⚠️ Session persistence failed, using in-memory persistence:",
            sessionError.message
          );

          try {
            // Final fallback to in-memory persistence (only survives current page)
            await setPersistence(auth, inMemoryPersistence);
            logger.log(
              "⚠️ Auth persistence set to IN-MEMORY (will NOT persist across refreshes)"
            );
            return false; // Return false to indicate limited persistence
          } catch (memoryError) {
            logger.error(
              "❌ All persistence methods failed:",
              memoryError.message
            );
            return false;
          }
        }
      }
    })();

    // Race between timeout and persistence setup
    return await Promise.race([persistencePromise, timeoutPromise]);
  } catch (error) {
    logger.error(
      "❌ Persistence setup failed with timeout or error:",
      error.message
    );
    // Fallback to in-memory persistence
    try {
      await setPersistence(auth, inMemoryPersistence);
      logger.log("⚠️ Fallback to in-memory persistence due to setup failure");
      return false;
    } catch (fallbackError) {
      logger.error(
        "❌ Even fallback persistence failed:",
        fallbackError.message
      );
      return false;
    }
  }
}

// Export a promise that resolves when persistence is set up
export const persistencePromise = setupAuthPersistence();

// Initialize auth persistence immediately when the module loads
persistencePromise.catch((error) => {
  logger.error("Failed to initialize auth persistence:", error);
});

export default app;
